import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CtrlIconComponent } from '@co-3/ctrl-ui/icons';
import { CtrlModalsService } from '@co-3/ctrl-ui/modals';
import { Contract } from '@models/contract';
import { ContractWidget } from '@models/contract-widgets';
import { getCurrentlyActivePaymentMethod, getQueuedPaymentMethods, PaymentMethod } from '@models/payment-method';
import {
    EditPaymentMethodModalComponent,
    EditPaymentMethodMode,
} from '../../modals/edit-payment-method-modal/edit-payment-method-modal.component';
import { PaymentMethodsService } from '../../payment-methods.service';
import {
    PortalCollapsibleComponent,
    PlaceholderCardComponent,
    PaymentMethodWidgetDetailsComponent,
} from '../../components';
import { finalize } from 'rxjs';
import { compareAsc } from 'date-fns';

@Component({
    selector: 'app-payment-method-widget',
    templateUrl: './payment-method-widget.component.html',
    styleUrl: './payment-method-widget.component.scss',
    imports: [
        PortalCollapsibleComponent,
        CtrlIconComponent,
        PaymentMethodWidgetDetailsComponent,
        PlaceholderCardComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentMethodWidgetComponent implements ContractWidget {
    public contract = input.required<Contract>();

    private paymentMethodsService = inject(PaymentMethodsService);
    private modalService = inject(CtrlModalsService);

    private paymentMethods = toSignal(this.paymentMethodsService.paymentMethodsForSelectedContract$);
    protected activePaymentMethod = computed(() => getCurrentlyActivePaymentMethod(this.paymentMethods()));
    protected queuedPaymentMethods = computed(() =>
        getQueuedPaymentMethods(this.paymentMethods()).sort((a, b) => compareAsc(a.validFrom, b.validFrom)),
    );

    protected pending = signal(false);

    public constructor() {
        effect(() => {
            const contract = this.contract();

            this.pending.set(true);
            untracked(() => {
                this.paymentMethodsService
                    .fetch(contract)
                    .pipe(
                        finalize(() => {
                            this.pending.set(false);
                        }),
                    )
                    .subscribe();
                this.paymentMethodsService.selectContract(contract);
            });
        });
    }

    protected editCurrentPaymentMethod(): void {
        this.openEditModal(this.activePaymentMethod(), 'editCurrent');
    }

    protected addQueuedPaymentMethod(): void {
        this.openEditModal(this.activePaymentMethod(), 'addQueued');
    }

    protected editQueuedPaymentMethod(paymentMethod: PaymentMethod): void {
        this.openEditModal(paymentMethod, 'editQueued');
    }

    protected deletePaymentMethod = (paymentMethod: PaymentMethod): void => {
        this.paymentMethodsService.delete(paymentMethod).subscribe();
    };

    private openEditModal(paymentMethod: PaymentMethod, mode?: EditPaymentMethodMode): void {
        this.modalService.open(EditPaymentMethodModalComponent, { paymentMethod, mode: mode }).subscribe();
    }
}
